/**
 * ValidationService
 *
 * Provides validation logic for form fields and models.
 * Consolidates validation from VideoConfigForm and useFormValidation hook.
 *
 * Extracted from:
 * - VideoConfigForm.tsx (lines 309-328, 419-425)
 * - useFormValidation.ts (entire hook logic)
 */

import type { SelectOption } from '../types';
import { DURATION_OPTIONS, LLM_PROVIDERS, TTS_PROVIDERS, RESOLUTIONS } from '../constants';

export class ValidationService {
  /**
   * Validates if a model is disabled and should not be selectable
   *
   * @param modelValue - Model ID to validate
   * @param availableModels - Array of available models with disabled flag
   * @returns true if model is disabled, false otherwise
   *
   * @example
   * isModelDisabled("gpt-4", models) // Returns true if model.disabled = true
   * isModelDisabled("gemma:7b", models) // Returns false if model.disabled = false
   */
  static isModelDisabled(modelValue: string, availableModels: SelectOption[]): boolean {
    const selectedModel = availableModels.find((m) => m.value === modelValue);
    return selectedModel?.disabled || false;
  }

  /**
   * Validates if a model selection is valid (not empty, not disabled)
   *
   * @param modelValue - Model ID to validate
   * @param availableModels - Array of available models
   * @returns Error message if invalid, null if valid
   *
   * @example
   * validateModelSelection("", models) // Returns "Please select a model."
   * validateModelSelection("gpt-4-disabled", models) // Returns "This model is coming soon..."
   * validateModelSelection("gemma:7b", models) // Returns null (valid)
   */
  static validateModelSelection(
    modelValue: string,
    availableModels: SelectOption[]
  ): string | null {
    // Validate that a model is selected
    if (!modelValue || modelValue === '') {
      return 'Please select a model.';
    }

    // Validate that the selected model is not disabled
    const selectedModel = availableModels.find((m) => m.value === modelValue);
    if (selectedModel?.disabled) {
      return 'This model is coming soon. Please select another model.';
    }

    return null; // Valid
  }

  /**
   * Validates if a duration selection is disabled (premium-only)
   *
   * @param durationMinutes - Duration value in minutes
   * @returns true if duration is disabled (premium), false otherwise
   *
   * @example
   * isDurationDisabled(1) // Returns false (free tier)
   * isDurationDisabled(15) // Returns true (premium tier)
   */
  static isDurationDisabled(durationMinutes: number): boolean {
    const durationOption = DURATION_OPTIONS.find((d) => d.value === durationMinutes);
    return durationOption?.disabled || false;
  }

  /**
   * Validates if a duration selection is allowed
   *
   * @param durationMinutes - Duration value in minutes
   * @returns Error message if invalid, null if valid
   *
   * @example
   * validateDurationSelection(1) // Returns null (valid)
   * validateDurationSelection(15) // Returns "This duration option is available..."
   */
  static validateDurationSelection(durationMinutes: number): string | null {
    const durationOption = DURATION_OPTIONS.find((d) => d.value === durationMinutes);
    if (durationOption?.disabled) {
      return 'This duration option is available for Premium users only. Please upgrade to access longer video durations.';
    }

    return null; // Valid
  }

  /**
   * Validates topic field
   *
   * @param topic - Topic string to validate
   * @returns Error message if invalid, undefined if valid
   *
   * @example
   * validateTopic("") // Returns "Topic is required"
   * validateTopic("A".repeat(600)) // Returns "Topic must be less than 500 characters"
   * validateTopic("Valid topic") // Returns undefined
   */
  static validateTopic(topic: string): string | undefined {
    // Type guard: ensure topic is a string
    if (!topic || typeof topic !== 'string' || topic.trim().length === 0) {
      return 'Topic is required';
    }
    if (topic.trim().length > 500) {
      return 'Topic must be less than 500 characters';
    }
    return undefined;
  }

  /**
   * Validates duration field
   *
   * @param duration - Duration in minutes (number, string, null, or undefined)
   * @returns Error message if invalid, undefined if valid
   *
   * @example
   * validateDuration(0.5) // Returns undefined (valid)
   * validateDuration(0) // Returns "Duration must be a positive number"
   * validateDuration(15) // Returns "Maximum duration is 10 minutes..."
   */
  static validateDuration(duration: number | string | null | undefined): string | undefined {
    // Handle null, undefined, or empty string
    if (
      duration === null ||
      duration === undefined ||
      duration === '' ||
      (typeof duration === 'string' && duration.trim() === '')
    ) {
      return 'Duration is required';
    }

    // Convert to number (handles strings like '2.5' and booleans)
    const durationNum = Number(duration);

    if (isNaN(durationNum) || durationNum <= 0) {
      return 'Duration must be a positive number';
    }

    if (durationNum < 0.25) {
      return 'Minimum duration is 15 seconds (0.25 minutes)';
    }

    if (durationNum > 10) {
      return 'Maximum duration is 10 minutes for free users. Upgrade to Premium for longer videos.';
    }

    return undefined;
  }

  /**
   * Validates resolution field
   *
   * @param resolution - Resolution string (e.g., "1920x1080")
   * @returns Error message if invalid, undefined if valid
   */
  static validateResolution(resolution: string): string | undefined {
    if (!resolution || typeof resolution !== 'string') {
      return 'Resolution is required';
    }

    const validResolutions = RESOLUTIONS.map((r) => r.value);

    if (!validResolutions.includes(resolution)) {
      return 'Invalid resolution selected';
    }

    return undefined;
  }

  /**
   * Validates language field
   *
   * @param language - Language code
   * @param validLanguages - Array of valid language codes
   * @returns Error message if invalid, undefined if valid
   */
  static validateLanguage(language: string, validLanguages: string[]): string | undefined {
    if (!language || typeof language !== 'string') {
      return 'Language is required';
    }

    // Handle common language code aliases for backward compatibility
    const languageAliases: Record<string, string> = {
      en: 'en-us',
      pt: 'pt-br',
      zh: 'cmn',
    };

    const normalizedLanguage = languageAliases[language] || language;

    if (!validLanguages.includes(normalizedLanguage)) {
      return 'Invalid language selected';
    }

    return undefined;
  }

  /**
   * Validates TTS provider field
   *
   * @param provider - TTS provider ID
   * @returns Error message if invalid, undefined if valid
   */
  static validateTTSProvider(provider: string): string | undefined {
    if (!provider || typeof provider !== 'string') {
      return 'TTS provider is required';
    }

    // Use centralized TTS_PROVIDERS constant - extract values from SelectOption[]
    const validProviders = TTS_PROVIDERS.map((p) => p.value);
    if (!validProviders.includes(provider)) {
      return 'Invalid TTS provider selected';
    }

    return undefined;
  }

  /**
   * Validates voice selection
   *
   * @param voiceId - Voice ID
   * @returns Error message if invalid, undefined if valid
   */
  static validateVoice(voiceId: string): string | undefined {
    if (!voiceId || typeof voiceId !== 'string') {
      return 'Voice selection is required';
    }

    return undefined;
  }

  /**
   * Validates video style field
   *
   * @param style - Video style value
   * @param validStyles - Array of valid style codes
   * @returns Error message if invalid, undefined if valid
   */
  static validateStyle(style: string, validStyles: string[]): string | undefined {
    if (!style || typeof style !== 'string') {
      return 'Video style is required';
    }

    if (!validStyles.includes(style)) {
      return 'Invalid style selected';
    }

    return undefined;
  }

  /**
   * Validates LLM provider field
   *
   * @param provider - LLM provider ID
   * @returns Error message if invalid, undefined if valid
   */
  static validateLLMProvider(provider: string): string | undefined {
    if (!provider || typeof provider !== 'string') {
      return 'LLM provider is required';
    }

    const validProviders = LLM_PROVIDERS.map((p) => p.value);
    if (!validProviders.includes(provider)) {
      return 'Invalid LLM provider selected';
    }

    return undefined;
  }

  /**
   * Validates LLM model field
   *
   * @param model - LLM model ID
   * @returns Error message if invalid, undefined if valid
   */
  static validateLLMModel(model: string): string | undefined {
    if (!model || typeof model !== 'string') {
      return 'LLM model is required';
    }

    return undefined;
  }
}
